import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context/AuthContext";
import { updateUserProfile } from "../../../services/api";
import { uploadProfileImage } from "../../../services/users";
import api from "../../../services/api";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import { GameButton, GameCard, GameHero } from "../../../components/game";

interface CoachProfile {
  id: string;
  user_id: string;
  certifications: string | null;
  bio: string | null;
  specialties: string[] | null;
}

export default function AppCoachProfile() {
  const { t, i18n } = useTranslation();
  const { user, refresh } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    full_name: "",
    locale: "ro",
    certifications: "",
    bio: "",
  });

  const [coachProfile, setCoachProfile] = useState<CoachProfile | null>(null);

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      try {
        // Get coach profile
        const { data } = await api.get<CoachProfile>("/coach/profile");
        setCoachProfile(data);
        setForm({
          full_name: user?.full_name || "",
          locale: user?.locale || "ro",
          certifications: data.certifications || "",
          bio: data.bio || "",
        });
      } catch (err) {
        // Profile might not exist yet
        setForm({
          full_name: user?.full_name || "",
          locale: user?.locale || "ro",
          certifications: "",
          bio: "",
        });
      }
      setLoading(false);
    };
    loadProfile();
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      // Update user profile
      await updateUserProfile({
        full_name: form.full_name,
        locale: form.locale,
      });

      // Update coach profile
      await api.patch("/coach/profile", {
        certifications: form.certifications || null,
        bio: form.bio || null,
      });

      // Change language if needed
      if (form.locale !== i18n.language) {
        i18n.changeLanguage(form.locale);
        localStorage.setItem("locale", form.locale);
      }

      await refresh();
      setSuccess(t("profile.saved", "Profilul a fost salvat!"));
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      console.error("Failed to save profile:", err);
      setError(err.response?.data?.error || t("profile.saveError", "Nu s-a putut salva profilul"));
    }
    setSaving(false);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError(t("profile.invalidImageType", "Te rog selectează o imagine validă"));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(t("profile.imageTooLarge", "Imaginea este prea mare. Maxim 5MB."));
      return;
    }

    try {
      setUploadingImage(true);
      setError("");
      const { url } = await uploadProfileImage(file);
      await updateUserProfile({ profile_url: url });
      await refresh();
      setSuccess(t("profile.imageUploaded", "Imaginea de profil a fost actualizată!"));
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      console.error("Failed to upload image:", err);
      setError(err.response?.data?.error || t("profile.imageUploadError", "Nu s-a putut încărca imaginea"));
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <MobileAppLayout>
      <div className="px-4 py-6 space-y-6">
        {/* Header */}
        <GameHero className="rounded-3xl px-6 py-7 text-center">
          {/* Profile Image with Upload */}
          <div className="relative w-28 h-28 mx-auto group">
            {(user as any)?.profile_url ? (
              <img
                src={(user as any).profile_url}
                alt={user?.full_name || "Profile"}
                className="w-28 h-28 rounded-full object-cover shadow-lg border-4 border-white/70"
              />
            ) : (
              <div className="w-28 h-28 rounded-full bg-white/15 flex items-center justify-center text-white text-3xl font-extrabold shadow-lg border-4 border-white/40">
                {user?.full_name?.charAt(0) || "A"}
              </div>
            )}
            {/* Upload Overlay */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage}
              className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            >
              {uploadingImage ? (
                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
            {/* Mobile Upload Button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage}
              className="absolute -bottom-1 -right-1 w-10 h-10 bg-white text-indigo-600 rounded-full flex items-center justify-center shadow-[0_3px_0_rgba(0,0,0,0.15)] active:translate-y-[2px] transition-transform sm:hidden"
            >
              {uploadingImage ? (
                <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>
          <h1 className="text-xl font-extrabold text-white mt-4">{user?.full_name}</h1>
          <p className="text-white/80 text-sm">{user?.email}</p>
          <span className="inline-block mt-3 px-3 py-1 bg-white/20 text-white text-sm font-bold rounded-full">
            {t("role.coach", "Antrenor")}
          </span>
        </GameHero>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-50 text-red-600 rounded-xl text-sm">{error}</div>
            )}
            {success && (
              <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl text-sm flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {success}
              </div>
            )}

            {/* Basic Info Card */}
            <GameCard className="overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                <h2 className="font-extrabold text-slate-800">{t("profile.basicInfo", "Informații de bază")}</h2>
              </div>
              <div className="p-4 space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {t("profile.fullName", "Nume complet")}
                  </label>
                  <input
                    type="text"
                    value={form.full_name}
                    onChange={e => setForm({ ...form, full_name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={t("profile.fullNamePlaceholder", "ex: Ion Popescu")}
                  />
                </div>

                {/* Language */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {t("profile.language", "Limba")}
                  </label>
                  <select
                    value={form.locale}
                    onChange={e => setForm({ ...form, locale: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="ro">Română</option>
                    <option value="en">English</option>
                  </select>
                </div>
              </div>
            </GameCard>

            {/* Coach Info Card */}
            <GameCard className="overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                <h2 className="font-extrabold text-slate-800">{t("profile.coachInfo", "Informații antrenor")}</h2>
              </div>
              <div className="p-4 space-y-4">
                {/* Certifications */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {t("profile.certifications", "Certificări")}
                  </label>
                  <textarea
                    value={form.certifications}
                    onChange={e => setForm({ ...form, certifications: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                    placeholder={t("profile.certificationsPlaceholder", "ex: Licență antrenor categoria A, UEFA B...")}
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    {t("profile.certificationsHint", "Adaugă certificările și licențele tale")}
                  </p>
                </div>

                {/* Bio */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {t("profile.bio", "Despre mine")}
                  </label>
                  <textarea
                    value={form.bio}
                    onChange={e => setForm({ ...form, bio: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                    placeholder={t("profile.bioPlaceholder", "Scrie o scurtă descriere despre tine și experiența ta...")}
                  />
                </div>
              </div>
            </GameCard>

            {/* Account Info */}
            <GameCard className="overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                <h2 className="font-extrabold text-slate-800">{t("profile.account", "Cont")}</h2>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-500">{t("profile.email", "Email")}</span>
                  <span className="text-sm text-slate-800 font-medium">{user?.email}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-t border-slate-100">
                  <span className="text-sm text-slate-500">{t("profile.role", "Rol")}</span>
                  <span className="text-sm text-indigo-600 font-bold">{t("role.coach", "Antrenor")}</span>
                </div>
              </div>
            </GameCard>

            {/* Submit Button */}
            <GameButton type="submit" variant="mint" disabled={saving}>
              {saving ? t("common.saving", "Se salvează...") : t("profile.save", "Salvează modificările")}
            </GameButton>
          </form>
        )}
      </div>
    </MobileAppLayout>
  );
}
